/**
 * myDayWorkItems.js
 *
 * Pure helpers for the "My day" work items of the sales agenda (`workItems` in GET /crm/follow-ups/agenda):
 * kind metadata (icon + i18n key), defensive normalization (old servers omit the keys), today/overdue merge,
 * totals for the metric tiles and the app-route mapping. No React / network imports — Jest-safe.
 *
 * @module helpers/myDayWorkItems
 */

import {MANAGEMENT_DETAIL_ROUTE, SALES_CUSTOMER_DETAIL_ROUTE} from '../config/salesRoutes';

/** Work item kinds in display order. */
export const WORK_KINDS = Object.freeze(['task', 'callback', 'ticket', 'appointment', 'workOrder', 'demo', 'mail']);

/** Stack route of the mail thread (opened from mail work items). */
export const MAIL_THREAD_ROUTE = 'MailThreadScreen';

/** Stack route of the work sheet (munkalap) detail. */
export const WORK_ORDER_DETAIL_ROUTE = 'WorkSheetDetailScreen';

/** Icon (MaterialCommunityIcons) + i18n key per kind. */
export const WORK_KIND_META = Object.freeze({
    task: {icon: 'clipboard-check-outline', labelKey: 'myday_kind_task'},
    callback: {icon: 'phone-in-talk', labelKey: 'myday_kind_callback'},
    ticket: {icon: 'lifebuoy', labelKey: 'myday_kind_ticket'},
    appointment: {icon: 'calendar-clock', labelKey: 'myday_kind_appointment'},
    workOrder: {icon: 'wrench', labelKey: 'myday_kind_workorder'},
    demo: {icon: 'presentation', labelKey: 'myday_kind_demo'},
    mail: {icon: 'email-alert-outline', labelKey: 'myday_kind_mail'},
});

const FALLBACK_META = Object.freeze({icon: 'dots-horizontal-circle-outline', labelKey: 'myday_kind_other'});

const asArray = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''));
const toMs = (v) => {
    const t = v ? new Date(v).getTime() : NaN;

    return Number.isNaN(t) ? null : t;
};

/**
 * Icon + i18n key of a kind (unknown kinds get a neutral fallback).
 * @param {string} kind work item kind.
 * @returns {{icon: string, labelKey: string}} meta.
 */
export const workKindMeta = (kind) => WORK_KIND_META[kind] || FALLBACK_META;

/**
 * Normalize one backend work item; returns null for unusable rows (no id or title).
 * @param {object} raw backend item.
 * @returns {object|null} `{kind, id, title, subtitle, dueAt, endAt, allDay, isOverdue, priority, status, entity}` or null.
 */
export const normalizeWorkItem = (raw) => {
    if (!raw || typeof raw !== 'object') {
        return null;
    }

    const id = str(raw._id || raw.id);
    const title = str(raw.title);

    if (!id && !title) {
        return null;
    }

    const e = raw.entity && typeof raw.entity === 'object' ? raw.entity : null;

    return {
        kind: str(raw.kind),
        id,
        title,
        subtitle: raw.subtitle && typeof raw.subtitle === 'object' ? '' : str(raw.subtitle),
        dueAt: raw.dueAt || null,
        endAt: raw.endAt || null,
        allDay: !!raw.allDay,
        isOverdue: !!raw.isOverdue,
        priority: str(raw.priority),
        status: str(raw.status),
        mail: raw.mail && typeof raw.mail === 'object' ? raw.mail : null,
        link: raw.link && typeof raw.link === 'object' ? raw.link : null,
        entity: e ? {type: str(e.type), id: str(e.id || e._id), name: str(e.name)} : null,
    };
};

const sortByDue = (list) => list.slice().sort((a, b) => (toMs(a.dueAt) ?? Infinity) - (toMs(b.dueAt) ?? Infinity));

/**
 * Safe `workItems` block: every list is an array of normalized items, `byKind` holds numbers.
 * @param {object} raw `agenda.workItems` (may be undefined on old servers).
 * @returns {{today: object[], overdue: object[], upcoming: object[], byKind: object}} normalized block.
 */
export const normalizeWorkItems = (raw) => {
    const r = raw && typeof raw === 'object' ? raw : {};
    const list = (v) => asArray(v).map(normalizeWorkItem).filter(Boolean);
    const byKind = {};

    WORK_KINDS.forEach((k) => { byKind[k] = Number(r.byKind?.[k]) || 0; });

    return {today: list(r.today), overdue: list(r.overdue), upcoming: list(r.upcoming), byKind};
};

/**
 * Overdue first (oldest first), then today's items by time; duplicates (same kind + id) removed.
 * @param {{today?: object[], overdue?: object[]}} workItems normalized block.
 * @returns {object[]} merged list; every row carries `isOverdue`.
 */
export const mergeWorkItems = (workItems) => {
    const w = workItems || {};
    const seen = new Set();
    const out = [];

    [...sortByDue(asArray(w.overdue)).map((i) => ({...i, isOverdue: true})), ...sortByDue(asArray(w.today))].forEach((i) => {
        const key = `${i.kind}:${i.id || i.title}`;

        if (!seen.has(key)) {
            seen.add(key);
            out.push(i);
        }
    });

    return out;
};

const msOf = (v) => {
    const n = v ? new Date(v).getTime() : NaN;

    return Number.isFinite(n) ? n : null;
};

const byAt = (a, b) => (a.at ?? Infinity) - (b.at ?? Infinity);

/**
 * One chronological plan of the day out of follow-ups, calendar events and work items, so the user sees WHAT to do
 * and WHEN instead of three lists per source. Overdue things come first (oldest first), then items without a fixed
 * time, then the timed schedule; `next` is the first timed entry that has not started yet (or is running now).
 * @param {object} agenda normalized agenda (follow-ups only when `access.followUps` is not false).
 * @param {number} [now] epoch ms.
 * @returns {{overdue: object[], anytime: object[], timed: object[], next: object|null}} entries `{key, type: 'followUp'|'event'|'work', at, endAt, item}`.
 */
export const buildDayTimeline = (agenda, now = Date.now()) => {
    const a = agenda || {};
    const fu = a.access?.followUps !== false;
    const overdue = [];
    const anytime = [];
    const timed = [];
    const add = (entry) => {
        if (entry.overdue) {
            overdue.push(entry);
        } else if (entry.at === null) {
            anytime.push(entry);
        } else {
            timed.push(entry);
        }
    };

    if (fu) {
        asArray(a.overdue).forEach((i) => add({key: `f${i._id}`, type: 'followUp', at: msOf(i.dueAt), endAt: null, overdue: true, item: {...i, isOverdue: true}}));
        asArray(a.today).forEach((i) => add({key: `f${i._id}`, type: 'followUp', at: msOf(i.dueAt), endAt: null, overdue: false, item: i}));
    }

    asArray(a.calendarEvents).forEach((ev) => add({
        key: `e${ev._id}`, type: 'event', at: ev.allDay ? null : msOf(ev.start), endAt: msOf(ev.end), overdue: false, item: ev,
    }));

    mergeWorkItems(a.workItems).forEach((w) => add({
        // Mail has no appointment time (dueAt = received time): it is an "answer today" item, not a slot.
        key: `w${w.kind}${w.id}`, type: 'work', at: (w.allDay || w.kind === 'mail') ? null : msOf(w.dueAt), endAt: msOf(w.endAt), overdue: !!w.isOverdue, item: w,
    }));

    overdue.sort(byAt);
    timed.sort(byAt);

    const next = timed.find((e) => e.at >= now || (e.endAt !== null && e.endAt > now)) || null;

    return {overdue, anytime, timed, next};
};

/**
 * Totals for the metric tiles: work items count alongside follow-ups; follow-up part is 0 without access.
 * @param {object} agenda normalized agenda.
 * @returns {{overdue: number, today: number, workOverdue: number, workToday: number}} totals.
 */
export const dayTotals = (agenda) => {
    const c = agenda?.counts || {};
    const fu = agenda?.access?.followUps !== false;
    const workOverdue = Number(c.workOverdue) || 0;
    const workToday = Number(c.workToday) || 0;

    return {
        overdue: (fu ? Number(c.overdue) || 0 : 0) + workOverdue,
        today: (fu ? Number(c.today) || 0 : 0) + workToday,
        workOverdue,
        workToday,
    };
};

/**
 * App route of a work item, or null when no real screen exists (the row is then not tappable).
 * Work orders open the work sheet; anything linked to a lead / customer opens that record.
 * @param {object} item normalized work item.
 * @returns {{name: string, params: object}|null} target.
 */
export const workItemRoute = (item) => {
    if (!item || typeof item !== 'object') {
        return null;
    }

    if (item.kind === 'workOrder' && item.id) {
        return {name: WORK_ORDER_DETAIL_ROUTE, params: {worksheetId: item.id}};
    }

    if (item.kind === 'mail' && item.id) {
        return {name: MAIL_THREAD_ROUTE, params: {messageId: item.id, threadKey: item.mail?.threadKey, subject: item.title}};
    }

    const e = item.entity;

    if (e && e.id) {
        if (e.type === 'lead') {
            return {name: MANAGEMENT_DETAIL_ROUTE, params: {type: 'leads', id: e.id}};
        }

        if (e.type === 'customer') {
            return {name: SALES_CUSTOMER_DETAIL_ROUTE, params: {customerId: e.id}};
        }
    }

    return null;
};
